/**
 * Columns that never appear in the Compliance Audit PDF export, even when selected in the table.
 * Portal is a navigation aid and Notes is internal working context; both stay fully available in
 * the XLS/CSV exports.
 */
export const PDF_EXCLUDED_COLUMN_IDS = ['portal', 'notes']

/**
 * Fixed precedence for filling the PDF's column budget, highest priority first. Mirrors the
 * Compliance Audit Customize menu order (ALL_CUSTOMIZABLE_COLUMN_IDS in the app's columnConfig.js)
 * minus PDF_EXCLUDED_COLUMN_IDS. Keep the two lists in sync if a customizable column is added,
 * removed, or reordered there.
 */
export const PDF_COLUMN_PRECEDENCE = [
  'compliance',
  'statusDetails',
  'categories',
  'rationale',
  'assessments',
  'license',
  'secureScore',
]

/** Max columns, including the always-visible Security Check + Status, that fit cleanly in the PDF. */
export const MAX_PDF_COLUMNS = 8

/** Default budget reserved for the always-visible Security Check + Status columns. */
export const DEFAULT_ALWAYS_VISIBLE_COLUMN_COUNT = 2

/** Compliance renders as two physical columns: Ref. # and Compliance. */
const COMPLIANCE_COLUMN_FIELD_COUNT = 2

/** Every non-Compliance column renders as a single physical column. */
const SINGLE_COLUMN_FIELD_COUNT = 1

const getColumnFieldCount = (columnId) =>
  columnId === 'compliance' ? COMPLIANCE_COLUMN_FIELD_COUNT : SINGLE_COLUMN_FIELD_COUNT

/**
 * Resolves which of the given selected column ids fit the PDF's column budget, in PDF column
 * order. Portal/Notes are never candidates (absent from PDF_COLUMN_PRECEDENCE). When the remainder
 * still exceeds the budget, columns are kept by PDF_COLUMN_PRECEDENCE so the result depends only on
 * which columns are selected, not the order they were selected in.
 */
export const getPdfVisibleColumnIds = (
  selectedColumnIds,
  alwaysVisibleColumnCount = DEFAULT_ALWAYS_VISIBLE_COLUMN_COUNT,
) => {
  const selected = new Set(selectedColumnIds)
  let remaining = MAX_PDF_COLUMNS - alwaysVisibleColumnCount

  return PDF_COLUMN_PRECEDENCE.filter((columnId) => {
    if (!selected.has(columnId)) {
      return false
    }

    const fieldCount = getColumnFieldCount(columnId)
    if (fieldCount > remaining) {
      return false
    }

    remaining -= fieldCount
    return true
  })
}

/** Selected columns that will be left out of the PDF: always-excluded ids, plus any cut for budget. */
export const getPdfExcludedColumnIds = (
  selectedColumnIds,
  alwaysVisibleColumnCount = DEFAULT_ALWAYS_VISIBLE_COLUMN_COUNT,
) => {
  const pdfVisibleIds = getPdfVisibleColumnIds(selectedColumnIds, alwaysVisibleColumnCount)
  return selectedColumnIds.filter((columnId) => !pdfVisibleIds.includes(columnId))
}
